import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { User, CalendarCheck, IndianRupee, GraduationCap, FileText } from "lucide-react";

/**
 * Staff profile — identity, attendance summary, leave balance, active loans,
 * training record, linked documents. Backend enforces role scoping.
 */
export default function StaffProfilePage() {
    const { staffId } = useParams();
    const [data, setData] = useState(null);

    async function load() {
        try {
            const r = await api.get(`/hr/staff/${staffId}/profile`);
            setData(r.data);
        } catch (e) {
            toast.error(apiError(e));
            setData({ error: apiError(e) });
        }
    }
    useEffect(() => { load(); }, [staffId]);

    if (!data) return <div className="p-6 text-sm text-gray-500" data-testid="staff-profile-loading">Loading profile…</div>;
    if (data.error) return <div className="p-6 text-sm text-red-600" data-testid="staff-profile-error">{data.error}</div>;

    const s = data.staff || {};
    const attn = data.attendance_last_30d || {};
    const lb = data.leave_balance || {};

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6" data-testid="staff-profile-page">
            <div className="rounded-xl bg-white border border-gray-200 p-4 mb-4 flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-[#1E3355] text-white flex items-center justify-center">
                    <User className="w-6 h-6" />
                </div>
                <div className="flex-1">
                    <div className="text-lg font-semibold text-[#1E3355]" data-testid="staff-name">{s.name || staffId}</div>
                    <div className="text-xs text-gray-600">
                        {s.role || "—"} · {data.site_name || s.site_id || "—"} · {s.agency || "direct"}
                    </div>
                    {s.phone && <div className="text-xs text-gray-600">Phone: {s.phone}</div>}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="staff-attendance-card">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-1">
                        <CalendarCheck className="w-4 h-4" /> Attendance (last 30d)
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div><div className="text-emerald-700 font-bold text-lg">{attn.present || 0}</div><div className="text-[10px] text-gray-500">Present</div></div>
                        <div><div className="text-red-700 font-bold text-lg">{attn.absent || 0}</div><div className="text-[10px] text-gray-500">Absent</div></div>
                        <div><div className="text-blue-700 font-bold text-lg">{attn.on_leave || 0}</div><div className="text-[10px] text-gray-500">On leave</div></div>
                    </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="staff-leave-card">
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Leave balance ({lb.year})</div>
                    <div className="text-sm">
                        <div>Quota: <b>{lb.annual_quota || 0}</b> days</div>
                        <div>Used YTD: <b>{lb.days_taken_ytd || 0}</b></div>
                        <div>Remaining: <b>{lb.days_remaining || 0}</b></div>
                    </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="staff-loans-card">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-1">
                        <IndianRupee className="w-4 h-4" /> Loans
                    </div>
                    <div className="text-sm">
                        <div>Active: <b>{(data.loans_active || []).length}</b></div>
                        <div>Total outstanding: <b>₹{(data.loans_active || []).reduce((a, l) => a + (l.outstanding || 0), 0).toLocaleString("en-IN")}</b></div>
                        <Link to="/loans" className="text-xs text-[#1E3355] underline" data-testid="staff-loans-link">Open loans queue →</Link>
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4" data-testid="staff-training-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
                    <GraduationCap className="w-4 h-4" /> Training record
                </div>
                {(data.training || []).length === 0 ? (
                    <div className="text-xs text-gray-500">No training attempts yet.</div>
                ) : (
                    <ul className="text-sm space-y-1">
                        {data.training.map((t) => (
                            <li key={t.id} data-testid={`staff-training-${t.id}`}>
                                {t.course_title || t.course_id} — {t.passed ? "✅ passed" : "❌ failed"} · {t.score}/{t.max_score} · {t.created_at?.slice(0, 10)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="staff-docs-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
                    <FileText className="w-4 h-4" /> Documents
                </div>
                {(data.documents || []).length === 0 ? (
                    <div className="text-xs text-gray-500">No documents tagged for this staff.</div>
                ) : (
                    <ul className="text-sm space-y-1">
                        {data.documents.map((d) => (
                            <li key={d.id} data-testid={`staff-doc-${d.id}`}>
                                <a href={d.url || "#"} target="_blank" rel="noreferrer" className="text-[#1E3355] underline">{d.name || d.filename || d.id}</a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
